import React, { useMemo, useState } from "react";
import { Plus, Sparkles, Trash2, Wand2, Save, RotateCcw, FileSearch, AlertTriangle, CheckCircle2, X, Flag, EyeOff, Eye, Pencil, Receipt } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import PricingSuggestPanel from "@/components/v2/PricingSuggestPanel";

const numberFmt = (n, currency = "INR") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(n || 0);

function MatchDialog({ open, onOpenChange, lineId, onAccepted }) {
  const [tab, setTab] = useState("similarity");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualQ, setManualQ] = useState("");
  const [manualResults, setManualResults] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !lineId) return;
    setTab("similarity");
    setManualQ("");
    setManualResults([]);
    setLoading(true);
    v2Api.boq
      .match(lineId, 5)
      .then(setMatches)
      .catch(() => toast.error("Could not fetch matches"))
      .finally(() => setLoading(false));
  }, [open, lineId]);

  const searchCatalog = async () => {
    const q = manualQ.trim();
    if (q.length < 2) return;
    setManualLoading(true);
    try {
      const results = await v2Api.boq.catalogSearch(q);
      setManualResults(results);
    } catch {
      toast.error("Catalogue search failed");
    } finally {
      setManualLoading(false);
    }
  };

  const renderCard = (m) => (
    <div
      key={m.sku_id}
      className="flex items-start justify-between gap-3 border rounded-md p-3 hover:border-blue-300"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
            {m.part_number}
          </span>
          <span className="font-medium truncate">{m.name}</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">{m.rationale}</p>
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
          {m.hsn_code && <span>HSN {m.hsn_code}</span>}
          <span>UoM {m.uom}</span>
          {m.base_price_inr && <span>Base ₹{m.base_price_inr.toLocaleString("en-IN")}</span>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <Badge
          className={
            m.score > 0.6
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : m.score > 0.3
              ? "bg-amber-100 text-amber-800 border-amber-200"
              : "bg-slate-100 text-slate-700 border-slate-200"
          }
        >
          {(m.score * 100).toFixed(0)}%
        </Badge>
        <Button
          size="sm"
          onClick={async () => {
            try {
              await v2Api.boq.acceptMatch(lineId, m.sku_id);
              toast.success(`Matched to ${m.part_number}`);
              onAccepted(m);
              onOpenChange(false);
            } catch {
              toast.error("Could not accept match");
            }
          }}
        >
          Use
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Catalogue SKU lookup</DialogTitle>
          <DialogDescription>
            Match this BOQ line to your product catalogue.
          </DialogDescription>
        </DialogHeader>

        {/* Tab bar */}
        <div className="flex gap-0 border-b -mt-1 mb-2">
          {[["similarity", "Similarity match"], ["manual", "Manual search"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "similarity" ? (
          loading ? (
            <p className="text-sm text-slate-500">Searching catalogue…</p>
          ) : matches.length === 0 ? (
            <p className="text-sm text-slate-500">No suggestions found.</p>
          ) : (
            <div className="space-y-2 max-h-[380px] overflow-y-auto">
              {matches.map(renderCard)}
            </div>
          )
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={manualQ}
                onChange={(e) => setManualQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchCatalog()}
                placeholder="Search product name or part number…"
                className="flex-1"
                autoFocus
              />
              <Button
                size="sm"
                onClick={searchCatalog}
                disabled={manualLoading || manualQ.trim().length < 2}
              >
                Search
              </Button>
            </div>
            {manualLoading ? (
              <p className="text-sm text-slate-500">Searching…</p>
            ) : manualResults.length === 0 && manualQ.trim() ? (
              <p className="text-sm text-slate-500">No results for "{manualQ}".</p>
            ) : (
              <div className="space-y-2 max-h-[340px] overflow-y-auto">
                {manualResults.map(renderCard)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const blankLine = (sl_no = 1) => ({
  id: null,
  sl_no,
  raw_description: "",
  raw_uom: "NOS",
  raw_qty: null,
  raw_expected_rate: null,
  source_ref: {},
  matched_sku_id: null,
  qty: 1,
  uom: "NOS",
  unit_rate: 0,
  discount_pct: 0,
  gst_rate_pct: 18,
  currency: "INR",
  fx_to_inr: 1,
  is_optional: false,
  is_flagged: false,
  is_deleted: false,
  notes: null,
  line_subtotal: 0,
  line_tax: 0,
  line_total: 0,
});

function MatchBadge({ isMatched, confidence }) {
  // Coloured pill: green ≥0.7, amber 0.55–0.7, slate 0.3–0.55, red <0.3
  if (!isMatched) {
    return (
      <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">unmatched</Badge>
    );
  }
  if (confidence == null) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">matched</Badge>
    );
  }
  const pct = Math.round(confidence * 100);
  let cls;
  if (confidence >= 0.7) cls = "bg-emerald-100 text-emerald-800 border-emerald-200";
  else if (confidence >= 0.55) cls = "bg-amber-100 text-amber-800 border-amber-200";
  else if (confidence >= 0.3) cls = "bg-orange-100 text-orange-800 border-orange-200";
  else cls = "bg-rose-100 text-rose-800 border-rose-200";
  return <Badge className={`${cls} text-[10px] font-mono`}>{pct}%</Badge>;
}

const recompute = (line) => {
  const gross = (line.qty || 0) * (line.unit_rate || 0);
  const subtotal = gross * (1 - (line.discount_pct || 0) / 100);
  const tax = subtotal * ((line.gst_rate_pct || 0) / 100);
  return {
    ...line,
    line_subtotal: Math.round(subtotal * 100) / 100,
    line_tax: Math.round(tax * 100) / 100,
    line_total: Math.round((subtotal + tax) * 100) / 100,
  };
};

function SourceBadge({ sourceRef, onRemove, onVerify, rfpId, tenderId }) {
  if (!sourceRef || Object.keys(sourceRef).length === 0) return null;

  const src = sourceRef.source || "";
  const page = sourceRef.page;
  const confidence = sourceRef.extractor_confidence;
  const quote = sourceRef.quote || "";
  const importRow = sourceRef.import_source_row;
  const sectionTitle = sourceRef.section_title || "";
  const sectionIndex = sourceRef.section_index;
  const sectionPageStart = sourceRef.section_page_start;
  const verified = !!sourceRef.verified;
  const isProjected = ["Manpower", "Quantity", "Maintenance"].includes(src);

  let label, cls, icon;
  if (importRow != null) {
    label = `Excel row ${importRow}`;
    cls = "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100";
  } else if (src === "Manual") {
    return null;
  } else if (verified) {
    label = page != null ? `p.${page} · Verified` : "Verified";
    cls = "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100";
    icon = <CheckCircle2 className="w-2.5 h-2.5" />;
  } else if (src === "Extracted") {
    label = page != null ? `p.${page} · Extracted` : "Extracted";
    cls = "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100";
    icon = <FileSearch className="w-2.5 h-2.5" />;
  } else if (isProjected) {
    label = `Brief · ${src}`;
    cls = "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100";
  } else if (src) {
    label = page != null ? `p.${page} · ${src}` : src;
    cls = "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100";
    icon = <FileSearch className="w-2.5 h-2.5" />;
  } else {
    return null;
  }

  const confPct = confidence != null ? Math.round(confidence * 100) : null;
  const confCls = confPct == null ? "" : confPct >= 70 ? "text-emerald-600" : confPct >= 45 ? "text-amber-600" : "text-rose-600";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`mt-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium cursor-pointer transition-colors ${cls}`}
        >
          {icon}{label}
          {confPct != null && <span className={`font-mono ${confCls}`}>{confPct}%</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-80 p-0 text-sm shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50 rounded-t-md">
          <span className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Source trace</span>
          {page != null && (
            <span className="text-xs text-slate-500">Page {page}</span>
          )}
        </div>

        <div className="p-3 space-y-3">
          {/* Origin */}
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium text-slate-500 w-20 shrink-0">Origin</span>
            <span>
              {src === "Extracted" ? "Extracted from RFP document" :
               src === "Manpower" ? "Projected from requirements brief (manpower)" :
               src === "Quantity" ? "Projected from requirements brief (quantities)" :
               src === "Maintenance" ? "Projected from requirements brief (maintenance)" :
               importRow != null ? `Imported from Excel (row ${importRow})` :
               src}
            </span>
          </div>

          {/* Confidence */}
          {confPct != null && (
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium text-slate-500 w-20 shrink-0">Confidence</span>
              <div className="flex items-center gap-1.5">
                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${confPct >= 70 ? "bg-emerald-500" : confPct >= 45 ? "bg-amber-400" : "bg-rose-400"}`}
                    style={{ width: `${confPct}%` }}
                  />
                </div>
                <span className={`font-mono font-semibold ${confCls}`}>{confPct}%</span>
                {confPct < 50 && (
                  <span className="flex items-center gap-0.5 text-rose-600">
                    <AlertTriangle className="w-3 h-3" /> Low — review carefully
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Section */}
          {(sectionTitle || sectionPageStart != null) && (
            <div className="rounded-md bg-slate-50 border border-slate-200 px-2.5 py-2 space-y-1">
              {sectionTitle && (
                <p className="text-xs font-medium text-slate-700 leading-tight">{sectionTitle}</p>
              )}
              <div className="flex items-center justify-between gap-2">
                {sectionPageStart != null && (
                  <span className="text-[10px] text-slate-500">
                    Page {sectionPageStart}{sourceRef.page_end && sourceRef.page_end !== sectionPageStart ? `–${sourceRef.page_end}` : ""}
                  </span>
                )}
                {tenderId && sectionIndex != null && (
                  <a
                    href={`/tender/${tenderId}?tab=brief&section=${sectionIndex}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-blue-600 hover:underline font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View section →
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Quote */}
          {quote ? (
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                Text from RFP
              </span>
              <blockquote className="border-l-2 border-blue-300 pl-2 text-xs text-slate-700 leading-relaxed bg-blue-50/50 py-1.5 pr-2 rounded-r italic">
                "{quote}"
              </blockquote>
            </div>
          ) : isProjected && !verified ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 text-xs text-amber-800 space-y-1.5">
              <p className="font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                Extracted from requirements brief, not a BOQ table
              </p>
              <p className="text-amber-700 leading-relaxed">
                The AI extractor may have missed this item's table in the PDF.
                If you can see it in the RFP as a priced table row, click{" "}
                <strong>Confirm from table</strong> below to mark it verified.
              </p>
            </div>
          ) : verified ? (
            <p className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Manually verified against RFP document.
            </p>
          ) : (
            <p className="text-xs text-slate-400 italic">No source quote available — re-sync from RFP to capture it.</p>
          )}

          {/* Actions */}
          <div className="pt-1 border-t flex items-center justify-between gap-2">
            {isProjected && !verified && onVerify && (
              <button
                type="button"
                onClick={onVerify}
                className="flex items-center gap-1.5 text-xs text-emerald-700 hover:text-emerald-900 font-medium"
              >
                <CheckCircle2 className="w-3 h-3" /> Confirm from table
              </button>
            )}
            {verified && onVerify && (
              <button
                type="button"
                onClick={onVerify}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 font-medium"
              >
                <X className="w-3 h-3" /> Undo verification
              </button>
            )}
            {!isProjected && !verified && <span />}
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-800 font-medium ml-auto"
              >
                <Trash2 className="w-3 h-3" /> Remove line
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function BoqGrid({ bid, project, refreshBid, rfpId, mode = "pricing", onGenerateQuot }) {
  const isBoqMode = mode === "boq";
  const { user } = useAuth();
  const [lines, setLines] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [matchOpenForLine, setMatchOpenForLine] = useState(null);
  const [suggestOpenForLine, setSuggestOpenForLine] = useState(null);
  const [marginAlerts, setMarginAlerts] = useState({});  // {line_id: alert | null}
  const [saving, setSaving] = useState(false);
  const currency = project?.currency || "INR";

  const refresh = async () => {
    if (!bid?.id) return;
    try {
      const rows = await v2Api.boq.list(bid.id);
      setLines(rows.length ? rows : []);
      setLoaded(true);
      setDirty(false);
    } catch {
      toast.error("Could not load BOQ");
    }
  };

  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid?.id]);

  React.useEffect(() => {
    // Recompute margin alerts whenever the persisted lines change. We only
    // call the endpoint for lines that have an id (saved) AND a unit_cost_inr —
    // alerts only kick in once cost is known.
    const candidates = lines.filter((l) => l.id && l.unit_cost_inr != null);
    if (candidates.length === 0) { setMarginAlerts({}); return; }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(candidates.map(async (l) => {
        try {
          const a = await v2Api.pricing.marginAlertForLine(l.id);
          next[l.id] = a || null;
        } catch { next[l.id] = null; }
      }));
      if (!cancelled) setMarginAlerts(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(lines.map((l) => [l.id, l.unit_cost_inr, l.unit_rate]))]);

  const totals = useMemo(() => {
    const active = lines.filter((l) => !l.is_deleted);
    const sub = active.reduce((a, l) => a + (l.line_subtotal || 0), 0);
    const tax = active.reduce((a, l) => a + (l.line_tax || 0), 0);
    const total = active.reduce((a, l) => a + (l.line_total || 0), 0);
    return { sub, tax, total };
  }, [lines]);

  const update = (idx, patch) => {
    setLines((rows) => {
      const next = [...rows];
      next[idx] = recompute({ ...next[idx], ...patch });
      return next;
    });
    setDirty(true);
  };

  const addLine = () => {
    setLines((rows) => [...rows, blankLine(rows.length + 1)]);
    setDirty(true);
  };

  const addManualLine = () => {
    const now = new Date().toISOString();
    setLines((rows) => [
      ...rows,
      {
        ...blankLine(rows.length + 1),
        source_ref: {
          source: "Manual",
          created_by_user_id: user?.id ?? null,
          created_by_name: user?.name || user?.email || "Unknown",
          created_at: now,
        },
      },
    ]);
    setDirty(true);
  };

  const removeLine = (idx) => {
    setLines((rows) => rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sl_no: i + 1 })));
    setDirty(true);
  };

  const save = async () => {
    if (!bid?.id) return;
    setSaving(true);
    try {
      const payload = lines.map((l, i) => ({
        id: l.id ?? undefined,
        sl_no: i + 1,
        raw_description: l.raw_description,
        raw_uom: l.raw_uom,
        raw_qty: l.raw_qty,
        raw_expected_rate: l.raw_expected_rate,
        source_ref: l.source_ref || {},
        matched_sku_id: l.matched_sku_id,
        match_confidence: l.match_confidence ?? null,
        match_method: l.match_method ?? null,
        qty: Number(l.qty) || 0,
        uom: l.uom || "NOS",
        unit_rate: Number(l.unit_rate) || 0,
        discount_pct: Number(l.discount_pct) || 0,
        gst_rate_pct: Number(l.gst_rate_pct) || 0,
        currency: l.currency || currency,
        fx_to_inr: Number(l.fx_to_inr) || 1,
        is_optional: !!l.is_optional,
        is_flagged: !!l.is_flagged,
        is_deleted: !!l.is_deleted,
        notes: l.notes,
        unit_cost_inr: l.unit_cost_inr === "" || l.unit_cost_inr == null ? null : Number(l.unit_cost_inr),
        proposed_unit_rate_inr: l.proposed_unit_rate_inr === "" || l.proposed_unit_rate_inr == null
          ? null : Number(l.proposed_unit_rate_inr),
      }));
      const next = await v2Api.boq.upsert(bid.id, payload);
      setLines(next);
      setDirty(false);
      toast.success("BOQ saved");
      refreshBid?.();
    } catch (err) {
      toast.error("Could not save BOQ");
    } finally {
      setSaving(false);
    }
  };

  // In pricing mode, soft-deleted lines are hidden entirely (they live in
  // the BOQ tab and are excluded from the bid). We track original indices so
  // update()/removeLine() still point at the right entry in `lines`.
  const displayLines = lines.map((l, i) => ({ line: l, origIdx: i }))
    .filter(({ line }) => isBoqMode || !line.is_deleted);

  if (!loaded) return <p className="text-sm text-slate-500">Loading BOQ…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">
            {isBoqMode ? "Customer Requirements (BOQ)" : "Bill of Quantities"}
          </h3>
          <p className="text-xs text-slate-500">
            {isBoqMode
              ? "Read-only buyer requirements from the RFP. Confirm catalogue matches and add internal notes. Pricing is managed in the Quotation tab."
              : <>Edit qty / brand / pricing inline. Use{" "}
                  <span className="inline-flex items-center gap-1 font-medium text-blue-600">
                    <Wand2 className="w-3 h-3" /> Suggest SKU
                  </span>{" "}
                  to map a line to your catalogue.</>
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={refresh} disabled={!dirty}>
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving}>
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : (isBoqMode ? "Save notes" : "Save")}
          </Button>
          {!isBoqMode && onGenerateQuot && (
            <Button
              size="sm"
              variant="default"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={saving}
              onClick={async () => {
                if (dirty) await save();
                onGenerateQuot();
              }}
            >
              <Receipt className="w-4 h-4 mr-1" /> Generate Quotation
            </Button>
          )}
        </div>
      </div>
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            {isBoqMode ? (
              <tr>
                <th className="px-2 py-2 w-10 text-right">Sl</th>
                <th className="px-2 py-2 text-left">Buyer Description</th>
                <th className="px-2 py-2 w-16 text-right">Qty</th>
                <th className="px-2 py-2 w-16">UoM</th>
                <th className="px-2 py-2 w-28 text-right" title="Expected rate from buyer's BOQ">Exp. Rate</th>
                <th className="px-2 py-2 w-48 text-center">Catalogue Match</th>
                <th className="px-2 py-2 text-left">Notes</th>
                <th className="px-2 py-2 w-20 text-center"></th>
              </tr>
            ) : (
              <tr>
                <th className="px-2 py-2 w-10 text-right">Sl</th>
                <th className="px-2 py-2 text-left">Description</th>
                <th className="px-2 py-2 w-16 text-right">Qty</th>
                <th className="px-2 py-2 w-16">UoM</th>
                <th className="px-2 py-2 w-28 text-right" title="Per-unit cost">Cost</th>
                <th className="px-2 py-2 w-28 text-right">Rate</th>
                <th className="px-2 py-2 w-14 text-right" title="(Rate − Cost) ÷ Rate">Margin</th>
                <th className="px-2 py-2 w-14 text-right">Disc %</th>
                <th className="px-2 py-2 w-14 text-right">GST %</th>
                <th className="px-2 py-2 w-28 text-right">Line total</th>
                <th className="px-2 py-2 w-20 text-center">Match</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayLines.length === 0 && (
              <tr>
                <td colSpan={isBoqMode ? 8 : 12} className="text-center text-slate-400 py-8">
                  No lines yet.{!isBoqMode && " Click \"Add line\" to start."}
                </td>
              </tr>
            )}
            {displayLines.map(({ line: l, origIdx }, i) => {
              const isManual = l.source_ref?.source === "Manual";
              const isFirstManual = isManual && (i === 0 || displayLines[i - 1]?.line.source_ref?.source !== "Manual");
              if (isBoqMode) {
                return (
                  <React.Fragment key={l.id || `tmp-${origIdx}`}>
                    {isFirstManual && (
                      <tr>
                        <td colSpan={8} className="px-3 py-2 bg-indigo-50 border-t-2 border-b border-dashed border-indigo-200">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1.5">
                              <Pencil className="w-3.5 h-3.5" /> Manual Items
                            </span>
                            <span className="text-[10px] text-indigo-400">These rows are never overwritten by Sync from RFP</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  <tr
                    className={`transition-colors ${
                      l.is_flagged
                        ? "bg-amber-50/30 border-l-2 border-l-amber-400"
                        : isManual
                        ? "bg-indigo-50/20 hover:bg-indigo-50/40"
                        : "hover:bg-slate-50/40"
                    }`}
                  >
                    <td className="px-2 py-2 text-right text-slate-500 align-top">{i + 1}</td>
                    <td className="px-2 py-2 align-top">
                      {isManual ? (
                        <Input
                          className="h-7 text-xs"
                          value={l.raw_description ?? ""}
                          onChange={(e) => update(origIdx, { raw_description: e.target.value })}
                          placeholder="Manual item description…"
                        />
                      ) : (
                        <p className="text-sm leading-snug text-slate-800">
                          {l.raw_description || <span className="text-slate-400 italic">No description</span>}
                        </p>
                      )}
                      {isManual && l.source_ref?.created_by_name && (
                        <p className="text-[10px] text-indigo-500 mt-0.5">
                          Added by {l.source_ref.created_by_name}
                          {l.source_ref.created_at && (
                            <> · {new Date(l.source_ref.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}</>
                          )}
                        </p>
                      )}
                      <SourceBadge
                        sourceRef={l.source_ref}
                        tenderId={rfpId || bid?.rfp_id}
                      />
                    </td>
                    <td className="px-2 py-2 text-right align-top font-mono text-slate-700">
                      {isManual ? (
                        <Input
                          type="number"
                          className="h-7 text-xs text-right w-20"
                          value={l.raw_qty ?? ""}
                          placeholder="Qty"
                          onChange={(e) => update(origIdx, { raw_qty: e.target.value === "" ? null : Number(e.target.value), qty: e.target.value === "" ? 1 : Number(e.target.value) })}
                        />
                      ) : (
                        l.raw_qty != null ? l.raw_qty : <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top text-slate-700">
                      {isManual ? (
                        <Input
                          className="h-7 text-xs w-20"
                          value={l.raw_uom ?? ""}
                          placeholder="UoM"
                          onChange={(e) => update(origIdx, { raw_uom: e.target.value, uom: e.target.value })}
                        />
                      ) : (
                        l.raw_uom || <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right align-top font-mono text-slate-700">
                      {isManual ? (
                        <Input
                          type="number"
                          className="h-7 text-xs text-right w-24"
                          value={l.raw_expected_rate ?? ""}
                          placeholder="Exp. Rate"
                          onChange={(e) => update(origIdx, { raw_expected_rate: e.target.value === "" ? null : Number(e.target.value) })}
                        />
                      ) : (
                        l.raw_expected_rate != null
                          ? numberFmt(l.raw_expected_rate, currency)
                          : <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center align-top">
                      <div className="flex flex-col items-center gap-1">
                        <MatchBadge isMatched={!!l.matched_sku_id} confidence={l.match_confidence} />
                        {l.suggested_sku_name && (
                          <span className="text-[10px] text-slate-600 text-center leading-tight max-w-[160px] truncate" title={l.suggested_sku_name}>
                            {l.suggested_sku_name}
                          </span>
                        )}
                        <button
                          type="button"
                          disabled={!l.id}
                          onClick={() => setMatchOpenForLine(l.id)}
                          className="text-[10px] text-blue-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                          title={!l.id ? "Save the BOQ first to match SKUs"
                                      : (l.matched_sku_id ? "Pick a different SKU" : "Find catalogue match")}
                        >
                          {l.matched_sku_id ? "change match" : "find match"}
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <Input
                        className="h-7 text-xs"
                        placeholder="Internal notes…"
                        value={l.notes ?? ""}
                        onChange={(e) => update(origIdx, { notes: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-2 align-top text-center">
                      <div className="flex items-center gap-1 justify-center">
                        <button
                          type="button"
                          onClick={() => update(origIdx, { is_flagged: !l.is_flagged })}
                          className={`${l.is_flagged ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                          title={l.is_flagged ? "Unflag" : "Flag for review"}
                        >
                          <Flag className="w-3.5 h-3.5" />
                        </button>
                        {isManual ? (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!await confirm("Permanently delete this manual item? This cannot be undone.", { title: "Delete manual item", confirmLabel: "Delete permanently" })) return;
                              if (l.id) {
                                try { await v2Api.boq.deleteLine(l.id); } catch { toast.error("Delete failed"); return; }
                              }
                              setLines((rows) => rows.filter((_, ri) => ri !== origIdx).map((r, ri) => ({ ...r, sl_no: ri + 1 })));
                              toast.success("Deleted");
                            }}
                            className="text-rose-400 hover:text-rose-600"
                            title="Permanently delete this manual item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => update(origIdx, { is_deleted: !l.is_deleted })}
                            className="text-slate-300 hover:text-slate-500"
                            title="Exclude from bid"
                          >
                            <EyeOff className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              }

              const cost = l.unit_cost_inr == null || l.unit_cost_inr === ""
                ? null : Number(l.unit_cost_inr);
              const rate = Number(l.unit_rate || 0);
              const marginPct =
                cost != null && rate > 0 ? ((rate - cost) / rate) * 100 : null;
              let marginCls = "text-slate-400";
              if (marginPct != null) {
                if (marginPct >= 15) marginCls = "text-emerald-600 font-semibold";
                else if (marginPct >= 6) marginCls = "text-amber-600 font-semibold";
                else marginCls = "text-rose-600 font-semibold";
              }
              return (
              <React.Fragment key={l.id || `tmp-${origIdx}`}>
              {isFirstManual && (
                <tr>
                  <td colSpan={12} className="px-3 py-2 bg-indigo-50 border-t-2 border-b border-dashed border-indigo-200">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1.5">
                        <Pencil className="w-3.5 h-3.5" /> Manual Items
                      </span>
                      <span className="text-[10px] text-indigo-400">These rows are never overwritten by Sync from RFP</span>
                    </div>
                  </td>
                </tr>
              )}
              <tr className={`transition-colors ${
                  l.is_flagged
                    ? "bg-amber-50/30 border-l-2 border-l-amber-400"
                    : isManual
                    ? "bg-indigo-50/20 hover:bg-indigo-50/40"
                    : "hover:bg-slate-50/40"
                }`}>
                <td className="px-2 py-1 text-right text-slate-500">{i + 1}</td>
                <td className="px-2 py-1">
                  <Input
                    className="h-8"
                    value={l.raw_description ?? ""}
                    onChange={(e) =>
                      update(origIdx, { raw_description: e.target.value })
                    }
                    placeholder={isManual ? "Manual item description…" : "As per buyer's tender table"}
                  />
                  {isManual && l.source_ref?.created_by_name && (
                    <p className="text-[10px] text-indigo-500 mt-0.5 leading-tight">
                      Added by {l.source_ref.created_by_name}
                      {l.source_ref.created_at && (
                        <> · {new Date(l.source_ref.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}</>
                      )}
                    </p>
                  )}
                  {!isManual && l.suggested_sku_name && (
                    <p className="text-[10px] text-emerald-700 mt-0.5 truncate leading-tight" title={l.suggested_sku_name}>
                      ↳ {l.suggested_sku_name}
                    </p>
                  )}
                  {/* Notes — carry forward from BOQ tab work; editable here too */}
                  <Input
                    className="h-6 text-[11px] mt-1 text-slate-500 placeholder:text-slate-300"
                    placeholder="Notes from BOQ…"
                    value={l.notes ?? ""}
                    onChange={(e) => update(origIdx, { notes: e.target.value })}
                  />
                  {!isManual && (
                    <SourceBadge
                      sourceRef={l.source_ref}
                      onRemove={() => removeLine(origIdx)}
                      onVerify={() => update(origIdx, {
                        source_ref: { ...(l.source_ref || {}), verified: !l.source_ref?.verified }
                      })}
                      tenderId={rfpId || bid?.rfp_id}
                    />
                  )}
                </td>
                <td className="px-2 py-1">
                  <Input
                    type="number"
                    className="h-8 text-right"
                    value={l.qty ?? ""}
                    onChange={(e) => update(origIdx, { qty: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Input
                    className="h-8"
                    value={l.uom ?? ""}
                    onChange={(e) => update(origIdx, { uom: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Input
                    type="number"
                    className="h-8 text-right text-slate-600"
                    placeholder="—"
                    title="Per-unit cost (auto-filled from catalog when SKU is matched)"
                    value={l.unit_cost_inr ?? ""}
                    onChange={(e) => update(origIdx, { unit_cost_inr: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Input
                    type="number"
                    className="h-8 text-right"
                    value={l.unit_rate ?? ""}
                    onChange={(e) => update(origIdx, { unit_rate: e.target.value })}
                  />
                  {l.suggested_unit_rate != null && Number(l.unit_rate || 0) === 0 && (
                    <button
                      type="button"
                      onClick={() => update(origIdx, { unit_rate: l.suggested_unit_rate })}
                      className="mt-0.5 text-[10px] text-emerald-700 hover:text-emerald-900 font-semibold leading-tight block"
                      title={`Pre-fill from matched SKU: ₹${Number(l.suggested_unit_rate).toLocaleString("en-IN")}`}
                    >
                      Use ₹{Number(l.suggested_unit_rate).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </button>
                  )}
                </td>
                <td className={`px-2 py-1 text-right font-mono text-xs ${marginCls}`}
                    title={marginPct == null
                      ? "Set unit cost to see margin"
                      : `Margin = (${rate} − ${cost}) / ${rate} = ${marginPct.toFixed(1)}%`}
                >
                  {marginPct == null ? "—" : `${marginPct.toFixed(1)}%`}
                  {marginAlerts[l.id] && (
                    <div className={`mt-0.5 text-[9px] px-1 py-0.5 rounded inline-block ${
                      marginAlerts[l.id].severity === "hard"
                        ? "bg-rose-100 text-rose-700 border border-rose-200"
                        : "bg-amber-100 text-amber-700 border border-amber-200"
                    }`} title={marginAlerts[l.id].message}>
                      {marginAlerts[l.id].severity === "hard" ? "BLOCK" : "WARN"}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1">
                  <Input
                    type="number"
                    className="h-8 text-right"
                    value={l.discount_pct ?? ""}
                    onChange={(e) => update(origIdx, { discount_pct: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1">
                  <Input
                    type="number"
                    className="h-8 text-right"
                    value={l.gst_rate_pct ?? ""}
                    onChange={(e) => update(origIdx, { gst_rate_pct: e.target.value })}
                  />
                </td>
                <td className="px-2 py-1 text-right font-mono">
                  {numberFmt(l.line_total, currency)}
                </td>
                <td className="px-2 py-1 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <MatchBadge isMatched={!!l.matched_sku_id} confidence={l.match_confidence} />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={!l.id}
                        onClick={() => setMatchOpenForLine(l.id)}
                        className="text-[10px] text-blue-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                        title={!l.id ? "Save the BOQ first to match SKUs"
                                    : (l.matched_sku_id ? "Pick a different SKU" : "Find SKU matches")}
                      >
                        {l.matched_sku_id ? "alt" : "suggest"}
                      </button>
                      <span className="text-slate-300">·</span>
                      <button
                        type="button"
                        disabled={!l.id}
                        onClick={() => setSuggestOpenForLine(suggestOpenForLine === l.id ? null : l.id)}
                        className="text-[10px] text-emerald-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                        title="Show pricing intelligence for this line"
                      >
                        {suggestOpenForLine === l.id ? "hide intel" : "intel"}
                      </button>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-1 text-center">
                  <div className="flex items-center gap-1 justify-center">
                    <button
                      type="button"
                      onClick={() => update(origIdx, { is_flagged: !l.is_flagged })}
                      className={`${l.is_flagged ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
                      title={l.is_flagged ? "Unflag" : "Flag for review"}
                    >
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                    {isManual ? (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!await confirm("Permanently delete this manual item? This cannot be undone.", { title: "Delete manual item", confirmLabel: "Delete permanently" })) return;
                          if (l.id) {
                            try { await v2Api.boq.deleteLine(l.id); } catch { toast.error("Delete failed"); return; }
                          }
                          setLines((rows) => rows.filter((_, ri) => ri !== origIdx).map((r, ri) => ({ ...r, sl_no: ri + 1 })));
                          toast.success("Deleted");
                        }}
                        className="text-rose-400 hover:text-rose-600"
                        title="Permanently delete this manual item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeLine(origIdx)}
                        className="text-slate-300 hover:text-red-500"
                        title="Remove line permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {suggestOpenForLine === l.id && l.id && (
                <tr key={`${l.id}-intel`}>
                  <td colSpan={12} className="p-0">
                    <PricingSuggestPanel
                      lineId={l.id}
                      onApplyRate={(price) => update(origIdx, { unit_rate: price })}
                    />
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
          {!isBoqMode && (
            <tfoot className="bg-slate-50/80 text-sm">
              <tr>
                <td colSpan={9} className="px-3 py-2 text-right text-slate-500">
                  Subtotal
                </td>
                <td className="px-2 py-2 text-right font-mono font-medium">
                  {numberFmt(totals.sub, currency)}
                </td>
                <td colSpan={2} />
              </tr>
              <tr>
                <td colSpan={9} className="px-3 py-2 text-right text-slate-500">
                  Tax (CGST+SGST or IGST per buyer state, computed on save)
                </td>
                <td className="px-2 py-2 text-right font-mono">
                  {numberFmt(totals.tax, currency)}
                </td>
                <td colSpan={2} />
              </tr>
              <tr className="border-t border-slate-200">
                <td colSpan={9} className="px-3 py-2 text-right font-semibold text-slate-700">
                  Grand total
                </td>
                <td className="px-2 py-2 text-right font-mono font-semibold text-blue-700">
                  {numberFmt(totals.total, currency)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex items-center gap-2">
        {!isBoqMode && (
          <Button variant="outline" size="sm" onClick={addLine}>
            <Plus className="w-4 h-4 mr-1" /> Add line
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={addManualLine}
          className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
        >
          <Pencil className="w-4 h-4 mr-1" /> Add manual item
        </Button>
      </div>

      <MatchDialog
        open={!!matchOpenForLine}
        lineId={matchOpenForLine}
        onOpenChange={(v) => !v && setMatchOpenForLine(null)}
        onAccepted={(m) => {
          // Optimistic update so badge flips immediately, before refresh round-trips
          setLines((rows) =>
            rows.map((l) =>
              l.id === matchOpenForLine
                ? {
                    ...l,
                    matched_sku_id: m.sku_id,
                    match_confidence: 1.0,
                    match_method: "manual",
                    suggested_sku_name: m.name,
                    suggested_unit_rate:
                      m.list_price_inr != null ? m.list_price_inr : m.base_price_inr,
                  }
                : l
            )
          );
          setDirty(true);
          refresh();
        }}
      />
    </div>
  );
}
